'use client';
import { Link, cn } from '@skin-seoul/design-system';
import { VerticalProductCardImage } from './VerticalProductCardImage';
import ProductCardInfoName from '../components/ProductCardInfoName';
import ProductCardInfoRating from '../components/ProductCardInfoRating';
import { ProductCardInfoPrice } from '../components/ProductCardInfoPrice';
import type { ProductCardProps } from '../ProductCard.types';
import { ProductCardProvider } from '../ProductCardContext';
import React from 'react';
import { VerticalProductCardContent } from './VerticalProductCardContent';
import ProductCardAddToCart from '../components/ProductCardAddToCart';
import ProductCardWishListButton from '../components/WishListButton';
import { extractPathname } from '@skin-seoul/utils';

export const VerticalProductCard = ({
  children,
  theme,
  onClick,
  orientation = 'vertical',
  wishListSlot,
  ...componentProps
}: ProductCardProps & {
  /**
   * Controls overlaid on the card that must NOT navigate — the wishlist heart.
   *
   * Rendered as a sibling of the link rather than inside it: an anchor's default navigation is not
   * cancelled by `stopPropagation`, and a `<button>` nested in an `<a>` is invalid interactive
   * content. Omitting it leaves the DOM byte-identical for every card that has no such control.
   */
  wishListSlot?: React.ReactNode;
}) => {
  const { href, linkComponent, className, imagePriority, ...restProps } = componentProps;
  const normalizedHref = extractPathname(href);
  const LinkComponent = linkComponent || Link;

  const productLink = (
    <LinkComponent
      className={cn(
        'flex flex-col gap-2 desktop:gap-4 w-full cursor-pointer min-w-0 no-underline hover:no-underline',
        className,
      )}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        onClick?.(e);
      }}
      href={normalizedHref}
      {...restProps}
    >
      <>{children}</>
    </LinkComponent>
  );

  return (
    <ProductCardProvider
      value={{
        ...restProps,
        href,
        theme,
        orientation,
        imagePriority,
      }}
    >
      {wishListSlot ? (
        // The image is the card's first child and spans its full width, so anchoring the slot to
        // the card's top-right lands on the same pixels as anchoring it to the image.
        <div className="relative w-full min-w-0">
          {productLink}
          {wishListSlot}
        </div>
      ) : (
        productLink
      )}
    </ProductCardProvider>
  );
};

VerticalProductCard.Content = VerticalProductCardContent;
VerticalProductCard.Image = VerticalProductCardImage;
VerticalProductCard.AddToCart = ProductCardAddToCart;
VerticalProductCard.WishListButton = ProductCardWishListButton;
VerticalProductCard.InfoName = ProductCardInfoName;
VerticalProductCard.InfoRating = ProductCardInfoRating;
VerticalProductCard.InfoPrice = ProductCardInfoPrice;
